import classNames from "classnames";
import { FC, useCallback, useEffect, useMemo } from "react";

import Modal from "components/Modal";

import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  resetCreateDeviceState,
  resetUpdateDeviceState,
} from "store/slices/deviceSlice";
import { getModalCallbacks } from "store/slices/modalCallbacksRegistry";
import { hideModal } from "store/slices/modalSlice";

import ModalCreateDeviceContent, { DeviceFormValues } from "./Content";

import styles from "./styles.module.scss";

type PropTypes = {
  isVisible: boolean;
  className?: string;
  mode?: "create" | "edit";
  id?: number;
  initialData?: Partial<DeviceFormValues>;
  callbackKey?: string;
};

const WidgetModalCreateDevice: FC<PropTypes> = ({
  isVisible,
  className,
  mode = "create",
  initialData,
  callbackKey,
}) => {
  const dispatch = useAppDispatch();
  const isCreating = useAppSelector((state: RootStore) => state.devices.isCreating);
  const isUpdating = useAppSelector((state: RootStore) => state.devices.isUpdating);
  const createError = useAppSelector((state: RootStore) => state.devices.createError);
  const updateError = useAppSelector((state: RootStore) => state.devices.updateError);

  const isCreate = mode === "create";

  const callbacks = useMemo(
    () => (callbackKey ? getModalCallbacks(callbackKey) : undefined),
    [callbackKey],
  );
  const onSubmit = useMemo(() => callbacks?.onSave, [callbacks]);
  const onUpdate = useMemo(
    () => (callbacks as { onUpdate?: (devEui: string, values: DeviceFormValues) => void } | undefined)?.onUpdate,
    [callbacks],
  );

  const title = isCreate ? "Add new device" : "Edit device";
  const description = isCreate
    ? "Register a new device to the system"
    : "Update device information";

  const handleClose = useCallback(() => {
    dispatch(hideModal());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetCreateDeviceState());
      dispatch(resetUpdateDeviceState());
    };
  }, [dispatch]);

  return (
    <Modal
      title={title}
      isClose
      isCloseBg
      isVisible={isVisible}
      className={classNames(styles.modal, className)}
    >
      <ModalCreateDeviceContent
        mode={mode}
        description={description}
        initialData={initialData}
        onSubmit={onSubmit}
        onUpdate={onUpdate}
        onClose={handleClose}
        isLoading={isCreate ? isCreating : isUpdating}
        submitError={isCreate ? createError : updateError}
      />
    </Modal>
  );
};

export default WidgetModalCreateDevice;
